import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, Smartphone, Tablet, Monitor, Settings } from 'lucide-react';
import { cn } from '../lib/utils';
import { RootState } from './utils/experienceStore';
import {
    setExperienceDeviceId,
    toggleExperienceOrientation,
} from './utils/state/canvasSlice';
import {
    DEVICE_DEFINITIONS,
    DeviceDefinition,
    getDeviceDimensions,
    getDeviceById,
} from './deviceDefinitions';
import useCustomDisplays from './hooks/useCustomDisplays';
import CustomDisplayDialog from './CustomDisplayDialog';

export default function DeviceSelector() {
    const dispatch = useDispatch();
    const canvasState = useSelector((state: RootState) => state.canvas);

    const [isOpen, setIsOpen] = useState(false);
    const [showCustomDisplayDialog, setShowCustomDisplayDialog] =
        useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    });
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Custom displays management
    const { customDisplays, addCustomDisplay, deleteCustomDisplay } =
        useCustomDisplays();

    // Combine built-in and custom devices
    const allDevices = useMemo(
        () => [...DEVICE_DEFINITIONS, ...customDisplays],
        [customDisplays],
    );

    // Get the current experience device definition (with custom display support)
    const currentDeviceConfig =
        canvasState.experienceDevices[canvasState.activeTab] ||
        canvasState.experienceDevices.main;

    const safeDevice = useMemo(() => {
        return getDeviceById(currentDeviceConfig.deviceId, customDisplays);
    }, [currentDeviceConfig.deviceId, customDisplays]);

    const safeOrientation = currentDeviceConfig.orientation;

    // Calculate dropdown position with edge detection
    const updateDropdownPosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdownHeight = 400; // Approximate height
            const dropdownWidth = 280; // Fixed width
            const padding = 8;

            let top = rect.bottom + window.scrollY;
            let left = rect.left + window.scrollX;

            // Check if dropdown would go off the bottom
            if (rect.bottom + dropdownHeight > window.innerHeight) {
                top = rect.top + window.scrollY - dropdownHeight;

                if (top < window.scrollY + padding) {
                    top = window.scrollY + padding;
                }
            }

            // Check if dropdown would go off the right edge
            if (rect.left + dropdownWidth > window.innerWidth) {
                left = rect.right + window.scrollX - dropdownWidth;

                if (left < window.scrollX + padding) {
                    left = window.scrollX + padding;
                }
            }

            // Ensure dropdown doesn't go off the left edge
            if (left < window.scrollX + padding) {
                left = window.scrollX + padding;
            }

            setDropdownPosition({
                top,
                left,
                width: dropdownWidth,
            });
        }
    };

    // Update position when opening dropdown
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            const handleResize = () => updateDropdownPosition();
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleResize, true);

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleResize, true);
            };
        }
    }, [isOpen]);

    const handleDeviceSelect = (device: DeviceDefinition) => {
        dispatch(
            setExperienceDeviceId({
                experienceName: canvasState.activeTab,
                deviceId: device.id,
            }),
        );
        setIsOpen(false);
    };

    const handleToggleOrientation = () => {
        dispatch(toggleExperienceOrientation(canvasState.activeTab));
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            updateDropdownPosition();
        }
    };

    const dimensions = getDeviceDimensions(safeDevice, safeOrientation);

    // Group devices by category
    const devicesByCategory = useMemo(() => {
        return allDevices.reduce((acc, device) => {
            if (!acc[device.category]) {
                acc[device.category] = [];
            }
            acc[device.category].push(device);
            return acc;
        }, {} as Record<string, DeviceDefinition[]>);
    }, [allDevices]);

    return (
        <div className="flex items-center gap-2">
            {/* Device Selector */}
            <div className="relative">
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={handleToggle}
                    className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground',
                        'bg-background border border-border rounded-md shadow-sm',
                        'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                        'transition-colors',
                    )}
                >
                    {safeDevice.category === 'iPhone' ? (
                        <Smartphone className="w-4 h-4" />
                    ) : safeDevice.category === 'Display' ? (
                        <Monitor className="w-4 h-4" />
                    ) : (
                        <Tablet className="w-4 h-4" />
                    )}
                    <span className="truncate max-w-32">{safeDevice.name}</span>
                    <span className="text-xs text-muted-foreground">
                        {dimensions.width} × {dimensions.height}
                    </span>
                    <ChevronDown
                        className={cn(
                            'w-4 h-4 text-muted-foreground transition-transform',
                            isOpen && 'rotate-180',
                        )}
                    />
                </button>

                {/* Portal-based dropdown */}
                {isOpen &&
                    createPortal(
                        <>
                            {/* Backdrop */}
                            <div
                                className="fixed inset-0 z-40"
                                onClick={() => setIsOpen(false)}
                            />

                            {/* Dropdown menu */}
                            <div
                                className="absolute z-50 bg-background border border-border rounded-md shadow-lg overflow-hidden"
                                style={{
                                    top: dropdownPosition.top,
                                    left: dropdownPosition.left,
                                    width: dropdownPosition.width,
                                }}
                            >
                                <div className="p-2 border-b border-border">
                                    <div className="text-xs font-medium text-foreground mb-1">
                                        Select Device
                                    </div>
                                </div>

                                <div className="max-h-80 overflow-y-auto">
                                    {Object.entries(devicesByCategory).map(
                                        ([category, devices]) => (
                                            <div key={category}>
                                                <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/50 border-b border-border">
                                                    {category}
                                                </div>
                                                {devices.map((device) => {
                                                    const deviceDimensions =
                                                        getDeviceDimensions(
                                                            device,
                                                            safeOrientation,
                                                        );
                                                    const isSelected =
                                                        device.id ===
                                                        safeDevice.id;

                                                    return (
                                                        <button
                                                            key={device.id}
                                                            type="button"
                                                            onClick={() =>
                                                                handleDeviceSelect(
                                                                    device,
                                                                )
                                                            }
                                                            className={cn(
                                                                'w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors',
                                                                'focus:outline-none focus:bg-muted',
                                                                'flex items-center justify-between',
                                                                isSelected &&
                                                                    'bg-blue-50 text-blue-700',
                                                            )}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {device.category ===
                                                                'iPhone' ? (
                                                                    <Smartphone className="w-4 h-4" />
                                                                ) : device.category ===
                                                                  'Display' ? (
                                                                    <Monitor className="w-4 h-4" />
                                                                ) : (
                                                                    <Tablet className="w-4 h-4" />
                                                                )}
                                                                <span className="font-medium">
                                                                    {
                                                                        device.name
                                                                    }
                                                                </span>
                                                            </div>
                                                            <span className="text-xs text-muted-foreground">
                                                                {
                                                                    deviceDimensions.width
                                                                }{' '}
                                                                ×{' '}
                                                                {
                                                                    deviceDimensions.height
                                                                }
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ),
                                    )}
                                </div>

                                {/* Manage Custom Displays Button */}
                                <div className="border-t border-border p-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCustomDisplayDialog(true);
                                            setIsOpen(false);
                                        }}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                    >
                                        <Settings className="w-4 h-4" />
                                        Manage Custom Displays
                                    </button>
                                </div>
                            </div>
                        </>,
                        document.body,
                    )}
            </div>

            {/* Custom Display Dialog */}
            <CustomDisplayDialog
                isOpen={showCustomDisplayDialog}
                onClose={() => setShowCustomDisplayDialog(false)}
                customDisplays={customDisplays}
                onAdd={addCustomDisplay}
                onDelete={deleteCustomDisplay}
            />

            {/* Orientation Toggle */}
            <button
                type="button"
                onClick={handleToggleOrientation}
                className={cn(
                    'p-2 text-foreground',
                    'bg-background border border-border rounded-md shadow-sm',
                    'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    'transition-colors',
                )}
                title={`Switch to ${
                    safeOrientation === 'portrait' ? 'landscape' : 'portrait'
                }`}
            >
                {safeOrientation === 'portrait' ? (
                    <Smartphone className="w-4 h-4" />
                ) : (
                    <div className="w-4 h-4 flex items-center justify-center">
                        <Smartphone className="w-4 h-4 transform rotate-90" />
                    </div>
                )}
            </button>
        </div>
    );
}
